/**
 * Shared cache for `get_my_active_institutional_enrollment` RPC.
 *
 * Both SubscriptionContext and useInstitutionalStudent need this enrollment row
 * to compute access. Without a shared cache they each issue the RPC on every
 * mount, doubling the cold-start cost.
 *
 * Exposes:
 *  - useActiveEnrollment(): React hook (subscribes to updates)
 *  - getActiveEnrollmentCached(queryClient, userId): imperative helper for
 *    non-hook callers (e.g. SubscriptionContext.checkSubscription) that respects
 *    the same react-query cache + staleTime.
 */
import { useQuery, type QueryClient } from "@tanstack/react-query";
import {
  fetchMyActiveInstitutionalEnrollment,
  type ActiveInstitutionalEnrollment,
} from "@/lib/institutional-enrollment";

export const ACTIVE_ENROLLMENT_STALE_MS = 60_000;
export const ACTIVE_ENROLLMENT_GC_MS = 5 * 60_000;

export const activeEnrollmentQueryKey = (userId: string | undefined) =>
  ["active-institutional-enrollment", userId ?? "anon"] as const;

export function useActiveEnrollment(userId: string | undefined) {
  return useQuery({
    queryKey: activeEnrollmentQueryKey(userId),
    queryFn: () => fetchMyActiveInstitutionalEnrollment(),
    enabled: !!userId,
    staleTime: ACTIVE_ENROLLMENT_STALE_MS,
    gcTime: ACTIVE_ENROLLMENT_GC_MS,
    refetchOnWindowFocus: false,
    refetchOnMount: true, // honors staleTime — no refetch within 60s
    retry: 1,
  });
}

/**
 * Imperative read used by non-hook code (Contexts). Returns the cached value
 * if fresh, otherwise issues the RPC and caches it. Both call sites share the
 * same cache entry so the network call happens at most once per staleTime
 * window per user.
 */
export async function getActiveEnrollmentCached(
  queryClient: QueryClient,
  userId: string | undefined
): Promise<ActiveInstitutionalEnrollment | null> {
  if (!userId) return null;
  return queryClient.fetchQuery({
    queryKey: activeEnrollmentQueryKey(userId),
    queryFn: () => fetchMyActiveInstitutionalEnrollment(),
    staleTime: ACTIVE_ENROLLMENT_STALE_MS,
    gcTime: ACTIVE_ENROLLMENT_GC_MS,
    retry: 1,
  });
}
